import * as B from '@blueprintjs/core';
import {useMutation} from '@tanstack/react-query';
import React, {useState} from 'react';

import Modal from 'app/components/Modal/Modal';
import {api} from 'app/modules/Remote';
import {ApiFeature} from 'app/modules/Remote/Feature';
import {formatFeatureName} from 'app/utils/featureUtils';

// Custom hook for combining features
const useCombineFeaturesMutation = (refreshFeatures: () => void) => {
  return useMutation({
    mutationFn: async ({
      featureCollectionId,
      newPropertyName,
      featureIds,
    }: {
      featureCollectionId: number;
      newPropertyName: string;
      featureIds: number[];
    }) => {
      return api.featureCollections
        .features(featureCollectionId)
        .combineFeatures(newPropertyName, featureIds);
    },
    onSuccess: () => {
      // Invalidate and refetch features for this feature collection
      refreshFeatures();
    },
  });
};

export const CombinePropertiesModal: React.FunctionComponent<
  React.PropsWithChildren<{
    isOpen: boolean;
    onClose: () => void;
    selectedFeatures: ApiFeature[];
    refreshFeatures: () => void;
  }>
> = ({isOpen, onClose, selectedFeatures, refreshFeatures}) => {
  const [newPropertyName, setNewPropertyName] = useState('');
  const combineFeaturesMutation = useCombineFeaturesMutation(refreshFeatures);

  const handleClose = () => {
    setNewPropertyName(''); // Reset form when closing
    onClose();
  };

  const onSubmit = async () => {
    if (!newPropertyName.trim()) {
      return; // Don't submit if name is empty
    }

    try {
      await combineFeaturesMutation.mutateAsync({
        featureCollectionId: selectedFeatures[0].properties.featureCollectionId,
        newPropertyName: newPropertyName.trim(),
        featureIds: selectedFeatures.map((f) => f.id),
      });

      // Close modal on success
      handleClose();
    } catch (error) {
      // Error handling is done by the mutation
      console.error('Failed to combine features:', error);
    }
  };

  const tooFewFeatures = selectedFeatures.length < 2;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      headerText="Merge Properties"
      primaryButton={{
        text: 'Submit',
        isLoading: combineFeaturesMutation.isPending,
        isDisabled: !newPropertyName.trim() || tooFewFeatures,
        onClick: onSubmit,
      }}
    >
      <div>
        <B.FormGroup label="New Property Name" style={{marginTop: '2rem'}}>
          <B.InputGroup
            type="text"
            value={newPropertyName}
            onChange={(e) => setNewPropertyName(e.target.value)}
          />
        </B.FormGroup>

        <B.Callout intent={B.Intent.WARNING}>
          <p>
            Merging will combine these properties or locations into one new property. All data,
            including notes and ordered imagery, will be moved to the new property.{' '}
            <b>This action cannot be undone.</b>
          </p>
          <p>Please confirm you want to combine the following sites:</p>
          <ul style={{listStyleType: 'unset', paddingLeft: '2rem'}}>
            {selectedFeatures.map((f) => (
              <li key={f.id}>{formatFeatureName(f)}</li>
            ))}
          </ul>
        </B.Callout>

        {tooFewFeatures && (
          <B.Callout intent={B.Intent.DANGER}>
            Please select at least two properties to merge.
          </B.Callout>
        )}

        {combineFeaturesMutation.isError && (
          <B.Callout intent={B.Intent.DANGER}>
            Error:{' '}
            {combineFeaturesMutation.error?.message ||
              'An unknown error occurred. Please try again or contact support.'}
          </B.Callout>
        )}
      </div>
    </Modal>
  );
};
